import React from "react";
import { StyleProp, StyleSheet, ViewStyle } from "react-native";
import { colors, layout } from "@/constants";
import Interactive from "./Interactive";
import Icon, { IconName, IconSize } from "./Icon";

/**
 * Icon tint for {@link IconButton}. The first three map to the standard `Icon`
 * tints; `"accent"` is the brand primary color (used by header actions).
 */
export type IconButtonColor = "primary" | "secondary" | "tertiary" | "accent";

/**
 * Props for the IconButton component
 */
interface IconButtonProps {
  /** The icon name to display */
  icon: IconName;
  /** Callback function triggered when the button is pressed */
  onPress: () => void;
  /** Accessibility label for screen readers */
  accessibilityLabel: string;
  /** Icon size - defaults to "medium" */
  size?: IconSize;
  /** Icon tint - defaults to "primary" */
  color?: IconButtonColor;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Optional style override for the pressable (e.g. padding adjustments) */
  style?: StyleProp<ViewStyle>;
}

/**
 * A low-emphasis, borderless icon button: a tappable `Icon` on a transparent
 * background with a hover/press state from the Interactive primitive. The
 * default everyday icon action (headers, inline actions, dialog affordances).
 *
 * For the prominent filled circular control (mic/speaker/connect) use
 * {@link ControlButton}.
 *
 * @param props - The component props
 * @returns A borderless icon button
 */
export default function IconButton({
  icon,
  onPress,
  accessibilityLabel,
  size = "medium",
  color = "primary",
  disabled = false,
  style
}: IconButtonProps) {
  return (
    <Interactive
      onPress={onPress}
      disabled={disabled}
      accessibilityLabel={accessibilityLabel}
      style={[styles.button, style]}
    >
      <Icon
        name={icon}
        size={size}
        color={color === "accent" ? "primary" : color}
        style={color === "accent" ? { color: colors.primary } : undefined}
      />
    </Interactive>
  );
}

const styles = StyleSheet.create({
  button: {
    alignItems: "center",
    justifyContent: "center",
    // `padding` shorthand (not axis keys) so callers can override the whole
    // box with `padding`, or trim one axis with `paddingVertical` etc.
    padding: layout.space.xs,
    borderRadius: layout.borderRadius.small,
    backgroundColor: "transparent"
  }
});
